/**
 * ISSUE-1057. Stages one PDF per message and drives it from a picked `File`
 * to a ready `mediaId` or an inline failure. Same `/upload/init` -> presigned
 * PUT -> `/upload/complete` sequence as `useComposerPhotoUpload.ts`, purpose
 * `chatPdf`, with the file name sent so `/message/list` and
 * `/chat/list-files` can show it.
 *
 * A run token guards every state write: remove, retry, reset and a new pick
 * each bump it, so a late result from an upload the fan already dropped never
 * overwrites a newer PDF.
 */
import { useUploadComplete, useUploadInit } from "@sportsmet/media-sdk/private";
import { useCallback, useRef, useState } from "react";

import { extractErrorCode } from "../../errors/apiErrorBus";
import {
  CHAT_PDF_CONTENT_TYPE,
  pdfRefusalFailure,
  pdfServerFailure,
  type typePdfUploadFailure,
  validatePdfFile,
} from "./composerPdfLogic";

export type typeComposerPdfAttachment = {
  file: File;
  state: "uploading" | "ready" | "failed";
  /** Non-null only when `state` is `ready`. */
  mediaId: string | null;
  /** Non-null only when `state` is `failed`. */
  failure: typePdfUploadFailure | null;
};

export function useComposerPdfUpload(): {
  attachment: typeComposerPdfAttachment | null;
  addFile: (file: File) => void;
  retry: () => void;
  remove: () => void;
  reset: () => void;
} {
  const uploadInit = useUploadInit();
  const uploadComplete = useUploadComplete();
  const [attachment, setAttachment] =
    useState<typeComposerPdfAttachment | null>(null);
  const attachmentRef = useRef<typeComposerPdfAttachment | null>(null);
  const runTokenRef = useRef(0);

  const setAndTrack = useCallback((next: typeComposerPdfAttachment | null) => {
    attachmentRef.current = next;
    setAttachment(next);
  }, []);

  const runUpload = useCallback(
    (file: File): void => {
      const token = runTokenRef.current;
      const isStale = (): boolean => token !== runTokenRef.current;
      const fail = (failure: typePdfUploadFailure): void => {
        if (isStale()) return;
        setAndTrack({ file, state: "failed", mediaId: null, failure });
      };

      async function run(): Promise<void> {
        const requestId = crypto.randomUUID();

        let init;
        try {
          init = await uploadInit.mutateAsync({
            purpose: "chatPdf",
            contentType: CHAT_PDF_CONTENT_TYPE,
            byteSize: file.size,
            fileName: file.name.length > 0 ? file.name : null,
          });
        } catch {
          fail(pdfServerFailure(null));
          return;
        }
        if (isStale()) return;
        if (init.error != null || init.data == null) {
          fail(pdfServerFailure(extractErrorCode(init)));
          return;
        }

        let putResponse: Response;
        try {
          putResponse = await fetch(init.data.uploadUrl, {
            method: init.data.uploadMethod,
            headers: { "content-type": init.data.uploadHeaders.contentType },
            body: file,
          });
        } catch {
          fail(pdfServerFailure(null));
          return;
        }
        if (isStale()) return;
        if (!putResponse.ok) {
          fail(pdfServerFailure(null));
          return;
        }

        let complete;
        try {
          complete = await uploadComplete.mutateAsync({
            mediaId: init.data.mediaId,
            requestId,
          });
        } catch {
          fail(pdfServerFailure(null));
          return;
        }
        if (isStale()) return;
        if (complete.error != null || complete.data == null) {
          fail(pdfServerFailure(extractErrorCode(complete)));
          return;
        }
        // A chatPdf publishes synchronously (no moderation), so any other
        // status is unexpected and treated as a retryable failure.
        if (complete.data.status !== "published") {
          fail(pdfServerFailure(null));
          return;
        }

        setAndTrack({
          file,
          state: "ready",
          mediaId: complete.data.mediaId,
          failure: null,
        });
      }

      void run();
    },
    [uploadInit, uploadComplete, setAndTrack],
  );

  const addFile = useCallback(
    (file: File) => {
      runTokenRef.current += 1;
      const refusal = validatePdfFile(file);
      if (refusal !== null) {
        setAndTrack({
          file,
          state: "failed",
          mediaId: null,
          failure: pdfRefusalFailure(refusal),
        });
        return;
      }
      setAndTrack({ file, state: "uploading", mediaId: null, failure: null });
      runUpload(file);
    },
    [runUpload, setAndTrack],
  );

  const retry = useCallback(() => {
    const current = attachmentRef.current;
    if (current?.state !== "failed" || current.failure?.canRetry !== true) {
      return;
    }
    runTokenRef.current += 1;
    setAndTrack({
      file: current.file,
      state: "uploading",
      mediaId: null,
      failure: null,
    });
    runUpload(current.file);
  }, [runUpload, setAndTrack]);

  const remove = useCallback(() => {
    runTokenRef.current += 1;
    setAndTrack(null);
  }, [setAndTrack]);

  return { attachment, addFile, retry, remove, reset: remove };
}
